import React, { useState, useContext, useEffect, useRef } from "react";
import { User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "react-oauth2-code-pkce";
import { REDIRECT_URI, LOGOUT_ENDPOINT } from "../authConfig";
import axios from "axios";
import { useLocation } from "react-router-dom";

export default function Header() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
const location = useLocation();

  // ---------------- STATE ----------------
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [profile, setProfile] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const hideHero =
  location.pathname.startsWith("/familyHome") ||
  location.pathname.startsWith("/profile");

  // Slideshow
  const heroImages = [
    "/images/family1.png",
    "/images/family2.png",
    "/images/family5.png",
    "/images/family3.png",
    "/images/family4.png",
    "/images/family6.png",
    "/images/family7.png",
    "/images/family8.png",
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // ---------------- REFS ----------------
  const dropdownRef = useRef(null);
  const userButtonRef = useRef(null);

  // ---------------- EFFECTS ----------------

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % heroImages.length
      );
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  // Fetch profile from backend
  const apiToken = auth.token || "";
  useEffect(() => {
    if (!auth || !auth.token) return;

    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${apiToken}` },
        });

        setProfile(res.data.data); // ← Correct way to access axios response

        console.log("Profile loaded:", res.data);

      } catch (error) {
        console.error("Profile fetch failed:", error);
      }
    };

    fetchProfile();
  }, [auth, API_BASE_URL, apiToken]);


  // ---------------- EARLY RETURN (this is allowed because NO hooks come after it) ----------------
  if (!auth || auth.isLoading) {
    return <div>Loading...</div>;
  }

  // ---------------- LOGIC ----------------

  const handleUserClick = () => setIsDropdownOpen((prev) => !prev);

  const handleProfileClick = () => {
    navigate("/profile");
    setIsDropdownOpen(false);
  };

  const handleHomeClick = () => {
    navigate("/");
    setIsDropdownOpen(false);
  };

  const handleLogoClick = () => navigate("/");

  const handleLogout = async () => {
    try {
      let idToken = localStorage.getItem("ROCP_idToken");
      if (idToken) idToken = idToken.replace(/^"(.*)"$/, "$1");

      // Clear PKCE tokens
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("pkce_")) sessionStorage.removeItem(key);
      });
      localStorage.clear();
      sessionStorage.clear();

      setIsLoggingOut(true);
      for (let i = countdown; i > 0; i--) {
        setCountdown(i);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      const redirectUri = encodeURIComponent(REDIRECT_URI);
      const logoutUrl = `${LOGOUT_ENDPOINT}?post_logout_redirect_uri=${redirectUri}&id_token_hint=${encodeURIComponent(
        idToken
      )}`;


      window.location.replace(logoutUrl);
    } catch (error) {
      console.error("Logout failed", error);
      setIsLoggingOut(false);
    }
  };

  // ---------------- DISPLAY NAME ----------------
  const token = auth.tokenData || {};

  const fullName =
    profile?.name ||
    token.name ||
    `${token.given_name ?? ""} ${token.family_name ?? ""}`.trim() ||
    token.preferred_username ||
    token.email ||
    "User";

  const currentImage = heroImages[currentImageIndex];

  // ---------------- RETURN ----------------
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-50/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">

          {/* LOGO */}
          <div onClick={handleLogoClick} className="flex items-center space-x-3 cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-white font-bold">FD</div>
            <span className="text-2xl font-semibold text-gray-900">FamilyDuniya</span>
          </div>

          {/* USER SECTION */}
          <div className="relative flex items-center gap-3">

            {/* Username */}
            <span className="hidden sm:inline text-gray-800 font-medium whitespace-nowrap">
              {fullName}
            </span>

            {/* Profile Image / Icon */}
            <button
              ref={userButtonRef}
              onClick={handleUserClick}
              className="w-11 h-11 rounded-full border border-gray-300 bg-gray-100 overflow-hidden flex items-center justify-center"
            >
              {profile?.avatar ? (
                <img src={profile.avatar} alt="User" className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-gray-700" />
              )}
            </button>

            {/* DROPDOWN MENU */}
            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 top-14 w-48 bg-white border border-gray-200 shadow-lg rounded-xl overflow-hidden animate-fadeInUp z-50"
              >
                <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-200">
                  {profile?.avatar ? (
                    <img src={profile.avatar} alt="User" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div className="text-sm font-semibold text-gray-900">{fullName}</div>
                </div>

                <button onClick={handleHomeClick} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100">
                  🏠 Home
                </button>
                <button onClick={handleProfileClick} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100">
                  👤 Profile
                </button>
                <button onClick={handleLogout} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600">
                  🚪 Logout
                </button>
              </div>
            )}

          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      {!hideHero && (
        <div className="bg-gray-50" id="headerImgDiv">
          <div className="relative mt-[80px] mx-2 sm:mx-4 md:mx-8 overflow-hidden rounded-xl shadow-lg">
            <img
              src={currentImage}
              alt="Family Hero"
              className="w-full h-[180px] sm:h-[250px] md:h-[320px] lg:h-[380px] object-cover brightness-90 transition-all"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <h1 className="text-white text-xl sm:text-2xl md:text-4xl lg:text-5xl font-semibold tracking-wide drop-shadow-lg text-center">
                Preserve Your Family’s Legacy
              </h1>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
